import React from 'react';

import {motion} from 'framer-motion';
import {useIntl} from 'react-intl';

export const AdaptiveTrainingDetailed: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const intl = useIntl();
  const phases = [
    {
      title: intl.formatMessage({
        id: 'home.adaptiveTrainingDetailed.phase1Title',
      }),
      description: intl.formatMessage({
        id: 'home.adaptiveTrainingDetailed.phase1Description',
      }),
      image: '/images/adaptive-training/foundation-phase.png',
      color: 'from-blue-500 to-blue-600',
      features: ['Easy pace runs', 'Aerobic development', 'Base building'],
    },
    {
      title: intl.formatMessage({
        id: 'home.adaptiveTrainingDetailed.phase2Title',
      }),
      description: intl.formatMessage({
        id: 'home.adaptiveTrainingDetailed.phase2Description',
      }),
      image: '/images/adaptive-training/test-phase.png',
      color: 'from-orange-500 to-orange-600',
      features: ['Fitness assessment', 'Power targets', 'Performance metrics'],
    },
    {
      title: intl.formatMessage({
        id: 'home.adaptiveTrainingDetailed.phase3Title',
      }),
      description: intl.formatMessage({
        id: 'home.adaptiveTrainingDetailed.phase3Description',
      }),
      image: '/images/adaptive-training/peak-phase.png',
      color: 'from-red-500 to-red-600',
      features: [
        'Race preparation',
        'Peak performance',
        'Event-specific training',
      ],
    },
  ];

  return (
    <section className="py-16 bg-gradient-to-br from-orange-50 to-yellow-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <motion.div className="text-center mb-12">
          <h2 className="text-4xl md:text-6xl font-light text-black mb-4">
            {intl.formatMessage({id: 'home.adaptiveTrainingDetailed.title'})}
          </h2>
          <p className="text-xl md:text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
            {intl.formatMessage({id: 'home.adaptiveTrainingDetailed.subtitle'})}
          </p>
        </motion.div>

        {/* Training Phases */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {phases.map((phase, index) => (
            <motion.div key={index} className="relative group">
              <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group-hover:scale-105">
                {/* Image */}
                {/* <div className="relative h-64 overflow-hidden">
                  <img
                    src={phase.image}
                    alt={phase.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                  <div
                    className={`absolute top-4 right-4 bg-gradient-to-r ${phase.color} text-white px-4 py-2 rounded-full text-sm font-semibold`}
                  >
                    Phase {index + 1}
                  </div>
                </div> */}

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-black mb-3">
                    {phase.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    {phase.description}
                  </p>

                  {/* Features */}
                  <div className="space-y-2">
                    {phase.features.map((feature, featureIndex) => (
                      <div
                        key={featureIndex}
                        className="flex items-center gap-2"
                      >
                        <div
                          className={`w-2 h-2 bg-gradient-to-r ${phase.color} rounded-full`}
                        ></div>
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Generated Workout Showcase */}
        <motion.div className="bg-white rounded-3xl p-8 shadow-lg">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-3xl font-bold text-black mb-4">
                {intl.formatMessage({
                  id: 'home.adaptiveTrainingDetailed.workoutSectionTitle',
                })}
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                {intl.formatMessage({
                  id: 'home.adaptiveTrainingDetailed.workoutSectionDescription',
                })}
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-full flex items-center justify-center">
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <span className="text-gray-700">Adapts to your schedule</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-full flex items-center justify-center">
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <span className="text-gray-700">
                    Matches your fitness level
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-full flex items-center justify-center">
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <span className="text-gray-700">Syncs to your watch</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <img
                src="/images/adaptive-training/AdaptiveTrainingExample1.png"
                alt="Generated Workout"
                className="w-full h-auto rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </motion.div>

        {/* Weekly Adaptation */}
        <motion.div className="mt-16 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-3xl p-8 text-white">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <img
                src="/images/adaptive-training/AdaptiveTrainingExample2.png"
                alt="Weekly Adaptation"
                className="w-full h-auto rounded-2xl shadow-lg"
              />
            </div>

            <div>
              <h3 className="text-3xl font-bold text-white mb-4">
                {intl.formatMessage({
                  id: 'home.adaptiveTrainingDetailed.weeklySectionTitle',
                })}
              </h3>
              <p className="text-lg text-white/90 leading-relaxed mb-6">
                {intl.formatMessage({
                  id: 'home.adaptiveTrainingDetailed.weeklySectionDescription',
                })}
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                  </div>
                  <span className="text-white font-medium">
                    Adjusts volume based on progress
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4"
                      />
                    </svg>
                  </div>
                  <span className="text-white font-medium">
                    Accounts for scheduled events
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                  </div>
                  <span className="text-white font-medium">
                    Optimizes for your goals
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
